import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'shared-ui';

import { fetchDodPromql, fetchDodTimeseries, fetchDodWorker } from 'api/dod/dod.v2';
import { DodTimeseriesResponse, DodTimeseriesSeries } from 'api/dod/dod.v2.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { BarDatum } from '../components/barPanel/BarPanel.types';
import { dodCommandLabel } from '../components/dodCommands';
import { rangeStepSeconds, useDodFilters } from '../hooks/useDodFilters/useDodFilters';
import { useDodOutlet } from '../hooks/useDodOutlet/useDodOutlet';
import { sumSeries, toPerMinute } from '../overviewTab/dodFormat';
import { formatSeconds, lastValue, promqlToSeries, seriesTotal } from '../retriesTab/retries.helpers';

import { WorkerTab } from './WorkerTab';
import { WorkerChartSource } from './WorkerTab.types';
import { WorkerProcessStatus } from './worker.helpers';

const EVENT_LIMIT = 50;

/**
 * `webkd_rcp_commands_total` reaches both sinks: `RcpCommandMetricsService` writes the Prometheus
 * family and the Redis rollup from the same call, so the command panels have a Redis source on
 * every deployment and a Prometheus source where one is configured. The rollup's fourth label slot
 * (`dim`) carries the command `result` (ok / error / timeout).
 *
 * Both queries group by the server so each connection card can show its own traffic. Whether that
 * label is actually present is decided by the response, not assumed: if no returned series carries
 * an `rcp_id`, the per-card panels are hidden and one worker-wide pair is rendered below the cards
 * instead. That keeps a deployment whose exporter predates the label honest rather than drawing
 * the same chart inside every card.
 */
const RCP_COMMANDS_METRIC = 'webkd_rcp_commands_total';

/** The rollup writes '-' for a label the sample did not carry; the charts treat it as absent. */
const ROLLUP_MISSING_LABEL = '-';

const rollupLabel = (value: string | undefined): string => (value && value !== ROLLUP_MISSING_LABEL ? value : '');

/**
 * Rollup series in the shape the Prometheus path produces: snake_case labels and a `key` of the
 * grouped values joined by '|', so everything downstream is source-agnostic. Counter buckets are
 * scaled to a per-minute rate like the overview does.
 */
const rollupToSeries = (
  response: DodTimeseriesResponse | undefined,
  dimLabel: 'result' | 'command',
): DodTimeseriesSeries[] =>
  (response?.series ?? []).map((series) => {
    const rcpId = rollupLabel(series.labels.rcpId);
    const dim = rollupLabel(dimLabel === 'result' ? series.labels.dim : series.labels.command);

    return {
      key: [rcpId, dim || (dimLabel === 'result' ? 'total' : 'unknown')].join('|'),
      labels: { rcp_id: rcpId, [dimLabel]: dim },
      points: toPerMinute(series, response?.step ?? 0),
    };
  });

/** Collapse several series onto one per distinct value of `label`, summing point-wise. */
const groupSum = (series: DodTimeseriesSeries[], label: string, fallback: string): DodTimeseriesSeries[] => {
  const groups = new Map<string, DodTimeseriesSeries[]>();
  series.forEach((one) => {
    const value = one.labels[label] || fallback;
    groups.set(value, [...(groups.get(value) ?? []), one]);
  });

  return Array.from(groups.entries()).map(([key, members]) => ({
    key,
    labels: { [label]: key },
    points: sumSeries(members),
  }));
};

const commandRateQuery = (stepSeconds: number): string =>
  `sum(rate(webkd_rcp_commands_total[${Math.max(stepSeconds, 60)}s])) by (rcp_id, result)`;

const commandsByTypeQuery = (stepSeconds: number): string =>
  `sum(rate(webkd_rcp_commands_total[${Math.max(stepSeconds, 60)}s])) by (rcp_id, command)`;

const commandP95Query = (stepSeconds: number): string =>
  `histogram_quantile(0.95, sum(rate(webkd_rcp_command_duration_seconds_bucket[${Math.max(
    stepSeconds,
    60,
  )}s])) by (le))`;

export const WorkerTabContainer = (): JSX.Element => {
  const { formatMessage } = useLocale();
  const { capabilities, refreshToken, setToolbarOptions } = useDodOutlet();
  const { range, refreshMs, resolveRange } = useDodFilters();

  const [source, setSource] = useState<WorkerChartSource>(capabilities.prometheus ? 'prometheus' : 'redis');

  const { data: worker, isLoading } = useQuery([QueryKeyEnum.DOD_WORKER, refreshToken], fetchDodWorker, {
    handleErrorManually: true,
    args: { limit: EVENT_LIMIT },
    refetchInterval: refreshMs,
  });

  const stepSeconds = rangeStepSeconds(range);
  const { fromTs, toTs } = resolveRange();
  const usePrometheus = capabilities.prometheus && source === 'prometheus';
  const useRollups = !usePrometheus && capabilities.timeseries;

  const { data: rateRollup, isLoading: isRateRollupLoading } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, RCP_COMMANDS_METRIC, 'rcpId,dim', range, refreshToken],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: {
        metric: RCP_COMMANDS_METRIC,
        from: fromTs,
        to: toTs,
        step: stepSeconds * 1000,
        groupBy: ['rcpId', 'dim'],
      },
      enabled: useRollups,
      refetchInterval: refreshMs,
      keepPreviousData: true,
    },
  );

  const { data: byTypeRollup, isLoading: isByTypeRollupLoading } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, RCP_COMMANDS_METRIC, 'rcpId,command', range, refreshToken],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: {
        metric: RCP_COMMANDS_METRIC,
        from: fromTs,
        to: toTs,
        step: stepSeconds * 1000,
        groupBy: ['rcpId', 'command'],
      },
      enabled: useRollups,
      refetchInterval: refreshMs,
      keepPreviousData: true,
    },
  );

  const promqlArgs = {
    start: Math.floor(fromTs / 1000),
    end: Math.floor(toTs / 1000),
    step: stepSeconds,
  };

  const { data: rateResult, isLoading: isRateLoading } = useQuery(
    [QueryKeyEnum.DOD_PROMQL, 'rcp-commands-rate', range, refreshToken],
    fetchDodPromql,
    {
      handleErrorManually: true,
      args: { ...promqlArgs, query: commandRateQuery(stepSeconds) },
      enabled: usePrometheus,
      refetchInterval: refreshMs,
    },
  );

  const { data: byTypeResult, isLoading: isByTypeLoading } = useQuery(
    [QueryKeyEnum.DOD_PROMQL, 'rcp-commands-by-type', range, refreshToken],
    fetchDodPromql,
    {
      handleErrorManually: true,
      args: { ...promqlArgs, query: commandsByTypeQuery(stepSeconds) },
      enabled: usePrometheus,
      refetchInterval: refreshMs,
    },
  );

  const { data: p95Result } = useQuery(
    [QueryKeyEnum.DOD_PROMQL, 'rcp-command-p95', range, refreshToken],
    fetchDodPromql,
    {
      handleErrorManually: true,
      args: { ...promqlArgs, query: commandP95Query(stepSeconds) },
      enabled: usePrometheus,
      refetchInterval: refreshMs,
    },
  );

  /**
   * Only the RCP select is published here: the worker heartbeat knows nothing about TKDs, and
   * offering an empty TKD list would be worse than offering none.
   */
  useEffect(() => {
    const connections = worker?.status?.connections ?? [];

    setToolbarOptions({
      rcp: connections.map((connection) => ({
        value: connection.rcpId,
        label: connection.rcpName || connection.rcpId,
      })),
    });
  }, [worker, setToolbarOptions]);

  const rateSeries = useMemo(
    () =>
      usePrometheus
        ? promqlToSeries(rateResult, (metric) => [metric.rcp_id ?? '', metric.result ?? 'total'].join('|'))
        : rollupToSeries(rateRollup, 'result'),
    [usePrometheus, rateResult, rateRollup],
  );

  const byTypeSeries = useMemo(
    () =>
      usePrometheus
        ? promqlToSeries(byTypeResult, (metric) => [metric.rcp_id ?? '', metric.command ?? 'unknown'].join('|'))
        : rollupToSeries(byTypeRollup, 'command'),
    [usePrometheus, byTypeResult, byTypeRollup],
  );

  /** True only when the source actually attributes a command to a server. */
  const hasPerServerCommands = useMemo(
    () => [...rateSeries, ...byTypeSeries].some((series) => !!series.labels.rcp_id),
    [rateSeries, byTypeSeries],
  );

  const commandSeries = useMemo<DodTimeseriesSeries[]>(
    () =>
      usePrometheus
        ? promqlToSeries(rateResult, (metric) => metric.result ?? 'total')
        : groupSum(rateSeries, 'result', 'total'),
    [usePrometheus, rateResult, rateSeries],
  );

  const commandSeriesLabels = useMemo(
    () =>
      commandSeries.reduce<Record<string, string>>(
        (labels, series) => ({
          ...labels,
          [series.key]: formatMessage({
            id: AppMessages[series.key === 'ok' ? 'dod.worker.series.commands' : 'dod.worker.series.errors'],
          }),
        }),
        {},
      ),
    [commandSeries, formatMessage],
  );

  const commandBars = useMemo<BarDatum[]>(
    () =>
      (usePrometheus
        ? promqlToSeries(byTypeResult, (metric) => metric.command ?? 'unknown')
        : // Raw bucket increments, not the per-minute rate: the bar is a count over the window.
          groupSum(
            (byTypeRollup?.series ?? []).map((series) => ({
              key: series.key,
              labels: { command: rollupLabel(series.labels.command) },
              points: series.points,
            })),
            'command',
            'unknown',
          )
      )
        .map((series) => ({
          id: series.key,
          label: dodCommandLabel(String(series.key).padStart(2, '0')),
          value: Math.round(seriesTotal(series)),
        }))
        .filter((bar) => bar.value > 0)
        .sort((left, right) => right.value - left.value),
    [usePrometheus, byTypeResult, byTypeRollup],
  );

  const seriesForRcp = useCallback(
    (source: DodTimeseriesSeries[], rcpId: string): DodTimeseriesSeries[] =>
      source
        .filter((series) => series.labels.rcp_id === rcpId)
        .map((series) => ({ ...series, key: series.key.split('|')[1] ?? series.key })),
    [],
  );

  const commandsP95Label = useMemo(() => {
    const value = lastValue(promqlToSeries(p95Result, () => 'p95')[0]);

    return `${formatMessage({ id: AppMessages['dod.retries.quantile.p95'] })} ${formatSeconds(value)}`;
  }, [p95Result, formatMessage]);

  return (
    <WorkerTab
      status={(worker?.status as WorkerProcessStatus | null) ?? null}
      ageMs={worker?.ageMs ?? null}
      events={worker?.events ?? []}
      isLoading={isLoading}
      now={Date.now()}
      source={source}
      onSourceChange={setSource}
      canUsePrometheus={capabilities.prometheus}
      grafanaUrl={capabilities.grafanaUrl}
      commandSeries={commandSeries}
      commandSeriesLabels={commandSeriesLabels}
      commandBars={commandBars}
      isCommandsLoading={
        usePrometheus ? isRateLoading || isByTypeLoading : isRateRollupLoading || isByTypeRollupLoading
      }
      areCommandsUnavailable={usePrometheus ? !capabilities.prometheus : !capabilities.timeseries}
      hasPerServerCommands={hasPerServerCommands}
      perServerCommandSeries={(rcpId) => seriesForRcp(rateSeries, rcpId)}
      perServerCommandBars={(rcpId) =>
        seriesForRcp(byTypeSeries, rcpId)
          .map((series) => ({
            id: series.key,
            label: dodCommandLabel(String(series.key).padStart(2, '0')),
            value: Math.round(seriesTotal(series)),
          }))
          .filter((bar) => bar.value > 0)
          .sort((left, right) => right.value - left.value)
      }
      commandsP95Label={commandsP95Label}
      timelineFrom={fromTs}
      timelineTo={toTs}
    />
  );
};
